import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api } from "../lib/api";
import { toast } from "sonner";
import Honeypot from "../components/Honeypot";
import { apiError } from "../lib/errors";
import { Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";

const inputCls = "bg-[#E2D4C1] border-[#E2D4C1] rounded-none focus-visible:ring-gold";
const EMPTY = { name: "", email: "", phone: "", vehicle_info: "", service_type: "full_vehicle_tint", message: "", website: "" };
const TOPICS = {
    tint: { label: "Tint Service", heading: "Tint Service Inquiry", type: "tint_service", button: "Request Quote",
        vehiclePlaceholder: "Your vehicle (year / make / model)", messagePlaceholder: "Tell us about the project…" },
    vehicle: { label: "Buying a Vehicle", heading: "Vehicle Sales Inquiry", type: "auto_sales", button: "Contact Sales",
        vehiclePlaceholder: "Vehicle you're looking for (make / model / budget)", messagePlaceholder: "Trade-in, financing, timeline, test drive…" },
};

export default function Contact() {
    const [params] = useSearchParams();
    const [topic, setTopic] = useState(params.get("topic") === "vehicle" ? "vehicle" : "tint");
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [settings, setSettings] = useState(null);
    useEffect(() => { api.get("/site/settings").then(r => setSettings(r.data)).catch(() => {}); }, []);
    const t = TOPICS[topic];

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const body = { type: t.type, ...form };
            if (topic === "vehicle") body.service_type = "vehicle_sales";
            await api.post("/inquiries", body);
            setSent(true);
            setForm(EMPTY);
        } catch (err) { toast.error(apiError(err, "Could not send inquiry")); }
        setSending(false);
    };

    return (
        <div data-testid="contact-page" className="max-w-7xl mx-auto px-6 md:px-12 py-24">
            <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-3">Get in Touch</div>
            <h1 className="font-display text-5xl md:text-6xl tracking-tighter mb-12">Speak with the shop.</h1>

            <div className="grid lg:grid-cols-[1fr_1.3fr] gap-12">
                <div className="space-y-8">
                    {settings?.address && (
                        <div>
                            <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-2">Showroom</div>
                            <div className="flex items-start gap-3 text-[#33302C]" data-testid="contact-address">
                                <MapPin size={18} aria-hidden="true" className="text-gold mt-1" />
                                <div>{settings.address}</div>
                            </div>
                        </div>
                    )}
                    {settings?.business_phone && (
                        <a href={`tel:${settings.business_phone.replace(/[^\d+]/g, "")}`} className="flex items-center gap-3 text-[#33302C] hover:text-gold" data-testid="contact-phone"><Phone size={18} aria-hidden="true" className="text-gold" /> {settings.business_phone}</a>
                    )}
                    {settings?.business_email && (
                        <a href={`mailto:${settings.business_email}`} className="flex items-center gap-3 text-[#33302C] hover:text-gold" data-testid="contact-email"><Mail size={18} aria-hidden="true" className="text-gold" /> {settings.business_email}</a>
                    )}
                    <div className="aspect-video bg-charcoal-800 border border-[#E2D4C1] mt-8 grid place-items-center text-[#6B645C] text-sm uppercase tracking-widest">Visit by appointment</div>
                </div>

                {sent ? (
                    <div data-testid="inquiry-sent" role="status" className="bg-charcoal-800 border border-gold/40 p-10 text-center self-start">
                        <CheckCircle2 aria-hidden="true" className="w-12 h-12 text-gold mx-auto mb-4" />
                        <h2 className="font-display text-3xl mb-3">Thank you, we've got it.</h2>
                        <p className="text-[#33302C] mb-6">Our team usually replies within one business day. Keep an eye on your inbox (and spam folder) for a reply from MBG.</p>
                        <button type="button" onClick={() => setSent(false)} className="text-xs uppercase tracking-[0.2em] text-gold hover:underline">Send another inquiry</button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="bg-charcoal-800 border border-[#E2D4C1] p-8 space-y-4" data-testid="tint-service-form" aria-labelledby="contact-form-heading">
                        <Honeypot value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
                        <div role="radiogroup" aria-label="What can we help with?" className="grid grid-cols-2 border border-[#E2D4C1]">
                            {Object.entries(TOPICS).map(([key, val]) => (
                                <button key={key} type="button" role="radio" aria-checked={topic === key} data-testid={`contact-topic-${key}`}
                                    onClick={() => setTopic(key)}
                                    className={`py-3 text-[11px] uppercase tracking-[0.2em] transition-colors ${topic === key ? "bg-gold text-white" : "text-[#33302C] hover:text-gold"}`}>
                                    {val.label}
                                </button>
                            ))}
                        </div>
                        <div id="contact-form-heading" className="text-[11px] tracking-[0.3em] uppercase text-gold pt-2">{t.heading}</div>
                        <Input aria-label="Full name" required autoComplete="name" data-testid="tint-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full name" className={inputCls} />
                        <Input aria-label="Email" required type="email" autoComplete="email" data-testid="tint-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inputCls} />
                        <Input aria-label="Phone (optional)" type="tel" autoComplete="tel" data-testid="tint-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className={inputCls} />
                        <Input aria-label={t.vehiclePlaceholder} data-testid="tint-vehicle" value={form.vehicle_info} onChange={e => setForm({ ...form, vehicle_info: e.target.value })} placeholder={t.vehiclePlaceholder} className={inputCls} />
                        {topic === "tint" && (
                            <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                                <SelectTrigger aria-label="Service type" data-testid="tint-service-type" className="bg-[#E2D4C1] border-[#E2D4C1] rounded-none focus:ring-gold"><SelectValue placeholder="Service" /></SelectTrigger>
                                <SelectContent className="bg-charcoal-800 border-[#E2D4C1] text-[#1A1A1A]">
                                    <SelectItem value="full_vehicle_tint">Full Vehicle Tint</SelectItem>
                                    <SelectItem value="windshield_only">Windshield Only</SelectItem>
                                    <SelectItem value="ceramic_upgrade">Ceramic Upgrade</SelectItem>
                                    <SelectItem value="removal_redo">Removal & Redo</SelectItem>
                                    <SelectItem value="commercial">Commercial / Building</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                        <Textarea aria-label="Message" rows={4} data-testid="tint-message" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder={t.messagePlaceholder} className={inputCls} />
                        <Button type="submit" disabled={sending} data-testid="tint-submit" className="w-full bg-gold hover:bg-gold-hover text-white rounded-none uppercase tracking-[0.2em] text-xs font-semibold py-5">{sending ? "Sending…" : t.button}</Button>
                    </form>
                )}
            </div>
        </div>
    );
}
